import { useState } from 'react'
import { kycApi } from '../../../services/api-client'
import type { VendorKYC, BusinessInfoData } from '../../../types'

interface Props {
  kyc: VendorKYC | null
  onComplete: (kyc: VendorKYC) => void
  onBack?: () => void
}

export function Step4Business({ kyc, onComplete, onBack }: Props) {
  const [formData, setFormData] = useState<BusinessInfoData>({
    business_name: kyc?.business_name || '',
    business_registration_number: kyc?.business_registration_number || '',
    business_type: kyc?.business_type || '',
    business_category: kyc?.business_category || '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await kycApi.submitStep4(formData)
      onComplete(response.kyc)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar información del negocio')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Información del Negocio
        </h2>
        <p className="text-gray-600">
          Proporcione detalles sobre su negocio o actividad comercial.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
            Nombre del Negocio <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="business_name"
            required
            value={formData.business_name}
            onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Mi Tienda Online S.A."
          />
        </div>

        <div>
          <label htmlFor="business_registration_number" className="block text-sm font-medium text-gray-700 mb-2">
            RFC / Número de Registro Fiscal (opcional)
          </label>
          <input
            type="text"
            id="business_registration_number"
            value={formData.business_registration_number}
            onChange={(e) => setFormData({ ...formData, business_registration_number: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="ABC123456XYZ"
          />
          <p className="text-sm text-gray-500 mt-1">
            RFC, Tax ID o número de registro empresarial
          </p>
        </div>

        <div>
          <label htmlFor="business_type" className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Negocio <span className="text-red-500">*</span>
          </label>
          <select
            id="business_type"
            required
            value={formData.business_type}
            onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Seleccione...</option>
            <option value="Persona Física">Persona Física (Autónomo)</option>
            <option value="Sociedad Anónima">Sociedad Anónima (S.A.)</option>
            <option value="Sociedad de Responsabilidad Limitada">Sociedad de Responsabilidad Limitada (S.R.L.)</option>
            <option value="Sociedad Cooperativa">Sociedad Cooperativa</option>
            <option value="Otro">Otro</option>
          </select>
        </div>

        <div>
          <label htmlFor="business_category" className="block text-sm font-medium text-gray-700 mb-2">
            Categoría de Productos/Servicios <span className="text-red-500">*</span>
          </label>
          <select
            id="business_category"
            required
            value={formData.business_category}
            onChange={(e) => setFormData({ ...formData, business_category: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Seleccione...</option>
            <option value="Ropa y Moda">Ropa y Moda</option>
            <option value="Electrónicos">Electrónicos</option>
            <option value="Hogar y Jardín">Hogar y Jardín</option>
            <option value="Deportes y Fitness">Deportes y Fitness</option>
            <option value="Belleza y Salud">Belleza y Salud</option>
            <option value="Juguetes y Niños">Juguetes y Niños</option>
            <option value="Alimentos y Bebidas">Alimentos y Bebidas</option>
            <option value="Libros y Medios">Libros y Medios</option>
            <option value="Arte y Artesanía">Arte y Artesanía</option>
            <option value="Joyería y Accesorios">Joyería y Accesorios</option>
            <option value="Automotriz">Automotriz</option>
            <option value="Mascotas">Mascotas</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> Esta información nos ayuda a entender mejor su negocio y personalizar su experiencia en la plataforma.
        </p>
      </div>

      <div className="flex justify-between pt-6 border-t">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Atrás
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${!onBack ? 'ml-auto' : ''}`}
        >
          {loading ? 'Guardando...' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
